import { chance, guessEdge, guessMultiplier, JACKPOT_STREAK, type HighLowState } from '../../engine/games/highLow';
import { money } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type ActionSpec, type TableProps } from './Frame';

const FLIP_MS = 550;
const LUCK_HOLD_MS = 1500;
const END_HOLD_MS = 250;

export function HighLowTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.highlow?.state as HighLowState | undefined;
  const anim = snapshot.anim;
  const luckFrame = secondChanceFrame(snapshot);
  // The wrong card a ★ second chance redrew ("Next: 7♣" in the engine's frame).
  const wrong = luckFrame ? cardsIn(luckFrame.split('\n').find((l) => l.startsWith('Next:')) ?? '')[0] ?? null : null;
  const luckMs = wrong !== null ? FLIP_MS + LUCK_HOLD_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, luckMs + FLIP_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();

  // The wrong card turns over first; then the second chance, then the redraw.
  useLuckMoment(snapshot, elapsed, wrong !== null ? FLIP_MS + 350 : null, () => ({ what: 'A wrong card is drawn again.', saved: true }), setBurst);

  const phase = state?.phase ?? 'idle';
  const trail = state?.trail ?? [];
  // While a guess animates: the card you guessed on, and the next one face
  // down, then turned (the wrong one first, if luck redrew it).
  const guessing = !!anim && trail.length >= 2;
  const base = guessing ? trail[trail.length - 2]! : state?.card;
  let next: { card?: number; hidden: boolean; wrong?: boolean } | null = null;
  if (guessing) {
    if (wrong !== null && elapsed < luckMs) next = elapsed < FLIP_MS ? { hidden: true } : { card: wrong, hidden: false, wrong: true };
    else next = elapsed - luckMs < FLIP_MS ? { hidden: true } : { card: state!.card, hidden: false };
  }
  const past = trail.slice(0, guessing ? -2 : -1).slice(-6);
  const cash = state ? Math.round(state.bet * state.value) : 0;
  const streaking = phase === 'streak' && !anim;

  const extra: ActionSpec[] = [];
  if (streaking && state) {
    for (const g of ['higher', 'lower'] as const) {
      const m = guessMultiplier(state.card, g, guessEdge(state));
      if (m > 0) extra.push({ id: `g-${g}`, label: `${g === 'higher' ? 'Higher' : 'Lower'} ×${m.toFixed(2)}`, onClick: () => actions.tableOption(g) });
    }
    extra.push({ id: 'cash', label: state.streak === 0 ? 'Take bet back' : `Cash out ${money(cash)}`, primary: true, onClick: () => actions.tableOption('cashout') });
  }

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (phase === 'done' && state && !anim) {
    const net = state.returned - state.bet;
    result = state.lost ? `Wrong: −${money(state.bet)} after a streak of ${state.streak}` : net > 0 ? `Cashed out +${money(net)} (streak ${state.streak})` : 'Took your bet back.';
    tone = state.lost ? 'loss' : net > 0 ? 'win' : '';
  }

  return (
    <>
      <Stage className="hilo-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={!!anim && elapsed < luckMs} />
        {/* Always there (empty at first), so the card below never moves. */}
        <div className="trail" aria-label="Earlier cards">
          {past.map((c, i) => <PlayingCard key={i} card={c} />)}
        </div>
        <div className="hilo-row">
          {phase === 'idle' || base === undefined ? (
            <PlayingCard hidden />
          ) : (
            <div className="hilo-card big-card"><PlayingCard card={base} ring={!!state?.lucky && !guessing && phase === 'streak'} /></div>
          )}
          {next && (
            <>
              <span className="hilo-arrow" aria-hidden="true">→</span>
              <div className={`hilo-card big-card${next.wrong ? ' wrong' : ''}`}>{next.hidden ? <PlayingCard hidden /> : <PlayingCard card={next.card} lost={next.wrong} />}</div>
            </>
          )}
        </div>
        {state && phase !== 'idle' && (
          <div className="hilo-facts">
            <span>Streak {state.streak} of {JACKPOT_STREAK} for the jackpot</span>
            {phase === 'streak' && <span>Worth {money(cash)}</span>}
            {streaking && (
              <span>
                Higher {Math.round(chance(state.card, 'higher') * 100)}% · Lower {Math.round(chance(state.card, 'lower') * 100)}% · same rank loses
              </span>
            )}
          </div>
        )}
        {phase === 'idle' && <p className="felt-hint">Deal a card, then call the next one.</p>}
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['higher', 'lower', 'cashout']} extra={extra} onAct={() => setBurst(null)} />
    </>
  );
}
